import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import { useApiQuery, resolveFileUrl } from "../api/client";
import "./ProjectVideoSection.css";

const formatTime = (sec) => {
  if (!Number.isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const ProjectVideoSection = () => {
  const videoRef = useRef(null);

  const { ref, inView } = useInView({
    threshold: 0.35,
    triggerOnce: false,
  });

  const { data, isLoading, isError } = useApiQuery("/videos?type=overview");
  const videos = data || [];
  const overviewVideo = videos[0];
  const videoSrc = overviewVideo ? resolveFileUrl(overviewVideo.videoUrl) : "";

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0); // 0-100
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  // Auto play/pause based on view
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;

    const handleAuto = async () => {
      if (inView) {
        try {
          v.muted = true; // autoplay safe
          await v.play();
          setIsPlaying(true);
          setIsMuted(true);
        } catch (e) {
          setIsPlaying(false);
        }
      } else {
        v.pause();
        setIsPlaying(false);
      }
    };

    handleAuto();
  }, [inView, videoSrc]);

  // Sync muted state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  const onLoadedMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrent(v.currentTime || 0);
    const pct = v.duration ? (v.currentTime / v.duration) * 100 : 0;
    setProgress(pct);
  };

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      try {
        await v.play();
        setIsPlaying(true);
      } catch (e) {}
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => setIsMuted((p) => !p);

  const seek = (e) => {
    const v = videoRef.current;
    if (!v) return;

    const value = Number(e.target.value); // 0-100
    const t = (value / 100) * (v.duration || 0);
    v.currentTime = t;
    setProgress(value);
  };

  const replay = async () => {
    const v = videoRef.current;
    if (!v) return;

    v.currentTime = 0;
    try {
      await v.play();
      setIsPlaying(true);
    } catch (e) {}
  };

  const fullscreen = async () => {
    const v = videoRef.current;
    if (!v) return;

    // Fullscreen API
    if (v.requestFullscreen) return v.requestFullscreen();
    if (v.webkitRequestFullscreen) return v.webkitRequestFullscreen();
    if (v.msRequestFullscreen) return v.msRequestFullscreen();
  };

  return (
    <section className="pvs" id="video" ref={ref}>
      <div className="pvs__container">
        <motion.div
          className="pvs__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="pvs__kicker">HB HEIGHT</div>
          <h2 className="pvs__title">HB Height Overview</h2>
          <p className="pvs__sub">
            Watch the 3D walkthrough to understand the structure, elevation, and the premium feel of the project.
          </p>
        </motion.div>

        <motion.div
          className="pvs__frame"
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.06 }}
        >
          <div className="pvs__media">
            {isLoading && <div className="pvs__state">Loading video…</div>}
            {isError && <div className="pvs__state">Couldn't load the video right now.</div>}
            {!isLoading && !isError && !videoSrc && (
              <div className="pvs__state">Overview video coming soon.</div>
            )}

            {videoSrc && (
              <>
                <video
                  ref={videoRef}
                  className="pvs__video"
                  src={videoSrc}
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={onLoadedMeta}
                  onTimeUpdate={onTimeUpdate}
                  onEnded={() => setIsPlaying(false)}
                />
                <div className="pvs__shade" />

                {/* Controls Overlay */}
                <div className="pvs__controls">
                  <div className="pvs__leftControls">
                    <Tooltip title={isPlaying ? "Pause" : "Play"} arrow>
                      <IconButton className="pvs__iconBtn" onClick={togglePlay}>
                        {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={isMuted ? "Unmute" : "Mute"} arrow>
                      <IconButton className="pvs__iconBtn" onClick={toggleMute}>
                        {isMuted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Replay" arrow>
                      <IconButton className="pvs__iconBtn" onClick={replay}>
                        <ReplayRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <div className="pvs__time">
                      <span>{formatTime(current)}</span>
                      <span className="pvs__timeSep">/</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  <div className="pvs__rightControls">
                    <Tooltip title="Fullscreen" arrow>
                      <IconButton className="pvs__iconBtn" onClick={fullscreen}>
                        <FullscreenRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </div>

                  <div className="pvs__progressWrap">
                    <input
                      className="pvs__range"
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={seek}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectVideoSection;
